import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2, GripVertical, ChevronUp, ChevronDown, Check, Film, AlertCircle, PartyPopper } from 'lucide-react';
import './ClipSortPage.css';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClipSortPage({ slug }) {
  const [data, setData] = useState(null);
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dragIdx = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/clip-sort/${slug}`)
      .then(res => {
        setData(res.data);
        setClips(res.data.clips || []);
        setSaved(res.data.status === 'saved');
      })
      .catch(err => setError(err.response?.data?.detail || 'This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const move = (from, to) => {
    if (to < 0 || to >= clips.length || from === to) return;
    setClips(prev => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
    setDirty(true);
    setSaved(false);
  };

  const handleDrop = (idx) => {
    if (dragIdx.current === null) return;
    move(dragIdx.current, idx);
    dragIdx.current = null;
    setDragOver(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/clip-sort/${slug}/save`, { order: clips.map(c => c.name) });
      setSaved(true);
      setDirty(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save — please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="csp csp-center">
        <Loader2 size={30} className="csp-spin" />
        <span>Loading your clips…</span>
      </div>
    );
  }

  if (error && !clips.length) {
    return (
      <div className="csp csp-center" data-testid="clipsort-error">
        <AlertCircle size={30} />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="csp" data-testid="clipsort-page">
      <header className="csp-header">
        <span className="csp-brand">n<span className="csp-brand-dot">!</span>checom</span>
        <h1 className="csp-title">Choose your clip order</h1>
        {data?.address && <p className="csp-address" data-testid="clipsort-address">{data.address}</p>}
        <p className="csp-instructions">
          Drag the clips (or use the arrows) into the order you'd like them to appear in your
          property video, then press <strong>Save order</strong>. Your editor will follow this sequence.
        </p>
      </header>

      <div className="csp-list" data-testid="clipsort-list">
        {clips.map((clip, idx) => (
          <div
            key={clip.name}
            className={`csp-card ${dragOver === idx ? 'is-dragover' : ''}`}
            draggable
            onDragStart={() => { dragIdx.current = idx; }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(idx); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={() => handleDrop(idx)}
            data-testid={`clipsort-card-${idx}`}
          >
            <span className="csp-index">{idx + 1}</span>
            <span className="csp-grip"><GripVertical size={16} /></span>
            <div className="csp-thumb">
              <video src={clip.url} muted playsInline preload="metadata" controls />
            </div>
            <div className="csp-meta">
              <span className="csp-name" title={clip.name}>{clip.name}</span>
              {clip.product_name && <span className="csp-product">{clip.product_name}</span>}
            </div>
            <div className="csp-arrows">
              <button onClick={() => move(idx, idx - 1)} disabled={idx === 0} data-testid={`clipsort-up-${idx}`} aria-label="Move up">
                <ChevronUp size={16} />
              </button>
              <button onClick={() => move(idx, idx + 1)} disabled={idx === clips.length - 1} data-testid={`clipsort-down-${idx}`} aria-label="Move down">
                <ChevronDown size={16} />
              </button>
            </div>
          </div>
        ))}
        {clips.length === 0 && (
          <div className="csp-center csp-empty"><Film size={26} /><span>No clips available for this link yet.</span></div>
        )}
      </div>

      {clips.length > 0 && (
        <div className="csp-savebar">
          {saved && !dirty ? (
            <span className="csp-saved-chip" data-testid="clipsort-saved-chip">
              <PartyPopper size={15} /> Order saved — your editor will use this sequence
            </span>
          ) : (
            <button className="csp-save-btn" onClick={save} disabled={saving} data-testid="clipsort-save-btn">
              {saving ? <Loader2 size={15} className="csp-spin" /> : <Check size={15} />}
              {saving ? 'Saving…' : 'Save order'}
            </button>
          )}
          {error && clips.length > 0 && <span className="csp-inline-error">{error}</span>}
        </div>
      )}
    </div>
  );
}
